import { type DocumentNode, gql } from '@apollo/client';

/**
 * Variables for accessSharedLinkMetadata mutation
 */
export interface AccessSharedLinkMetadataVariables {
  id: string;
  password?: string;
}

/**
 * Custom field definition structure
 */
export interface SharedCustomFieldDefinition {
  id: string;
  name: string;
  fieldType: string;
}

/**
 * Custom field node structure
 */
export interface SharedCustomFieldNode {
  id: string;
  definition: SharedCustomFieldDefinition;
  content: string;
}

/**
 * Custom field edge structure
 */
export interface SharedCustomFieldEdge {
  node: SharedCustomFieldNode;
}

/**
 * Custom field set connection structure
 */
export interface SharedCustomFieldSetConnection {
  edges: SharedCustomFieldEdge[];
}

/**
 * Format node structure
 */
export interface SharedFormatNode {
  id: string;
  mediapath: string;
  filename: string;
  formatType: string;
}

/**
 * Format edge structure
 */
export interface SharedFormatEdge {
  node: SharedFormatNode;
}

/**
 * Page info structure
 */
export interface SharedPageInfo {
  startCursor: string | null;
  endCursor: string | null;
}

/**
 * Format set connection structure
 */
export interface SharedFormatSetConnection {
  pageInfo: SharedPageInfo;
  edges: SharedFormatEdge[];
}

/**
 * Asset structure within shared link metadata
 */
export interface SharedLinkAsset {
  id: string;
  name: string;
  inpoint: number | null;
  outpoint: number | null;
  duration: number | null;
  timecode: string | null;
  language: string | null;
  mediasize: number | null;
  mediatype: string | null;
  createdAt: string;
  lastModified: string;
  customfieldSet: SharedCustomFieldSetConnection;
  formatSet: SharedFormatSetConnection;
}

/**
 * Shared link metadata structure
 */
export interface SharedLinkMetadata {
  id: string;
  assetId: string | null;
  asset: SharedLinkAsset | null;
  collectionId: string | null;
  downloadable: boolean;
  expirationDatetime: string;
}

/**
 * Access shared link metadata payload structure
 */
export interface AccessSharedLinkMetadataPayload {
  sharedLinkMetadata: SharedLinkMetadata;
}

/**
 * Response structure for accessSharedLinkMetadata mutation
 */
export interface AccessSharedLinkMetadataResponse {
  accessSharedLinkMetadata: AccessSharedLinkMetadataPayload;
}

const accessSharedLinkMetadata: DocumentNode = gql`
  mutation accessSharedLinkMetadata($id: String!, $password: String) {
    accessSharedLinkMetadata(id: $id, password: $password) {
      sharedLinkMetadata {
        id
        assetId
        asset {
          id
          name
          inpoint
          outpoint
          duration
          timecode
          language
          mediasize
          mediatype
          createdAt
          lastModified
          customfieldSet {
            edges {
              node {
                id
                definition {
                  id
                  name
                  fieldType
                }
                content
              }
            }
          }
          formatSet {
            pageInfo {
              startCursor
              endCursor
            }
            edges {
              node {
                id
                mediapath
                filename
                formatType
              }
            }
          }
        }
        collectionId
        downloadable
        expirationDatetime
      }
    }
  }
`;

export default accessSharedLinkMetadata;
